import api from './api';

let profileRequest = null;

export const authService = {
  async sendVerificationCode(email, username, captchaVerifyParam) {
    const response = await api.post('/auth/send-code', { email, username, captchaVerifyParam });
    return response.data;
  },

  async forgotPassword(email, captchaVerifyParam) {
    const response = await api.post('/auth/forgot-password', { email, captchaVerifyParam });
    return response.data;
  },

  async resetPassword(email, code, password) {
    const response = await api.post('/auth/reset-password', { email, code, password });
    return response.data;
  },

  async register(username, email, password, verificationCode) {
    const response = await api.post('/auth/register', { username, email, password, verificationCode });
    return response.data;
  },

  async login(email, password, captchaVerifyParam) {
    const response = await api.post('/auth/login', { email, password, captchaVerifyParam });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      this.setCurrentUser(response.data.user);
    }
    return response.data;
  },

  getProfile() {
    if (!profileRequest) {
      profileRequest = api.get('/auth/profile')
        .then(response => response.data)
        .finally(() => {
          profileRequest = null;
        });
    }

    return profileRequest;
  },

  async getAllUsers(page = 1, limit = 10, search = '') {
    const response = await api.get('/auth/users', {
      params: { page, limit, search }
    });
    return response.data;
  },

  async updateUserRole(userId, role) {
    const response = await api.put(`/auth/users/${userId}/role`, { role });
    return response.data;
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new Event('auth:userUpdated'));
  },

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  setCurrentUser(user) {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
    window.dispatchEvent(new Event('auth:userUpdated'));
  },

  isAuthenticated() {
    return !!localStorage.getItem('token');
  }
};

export const settingsService = {
  async getRegistrationStatus() {
    const response = await api.get('/settings/registration');
    return response.data;
  },

  async updateRegistrationStatus(isOpen) {
    const response = await api.put('/settings/registration', { isOpen });
    return response.data;
  },

  async getCaptchaConfig() {
    const response = await api.get('/settings/captcha');
    return response.data;
  }
};

export const playlistService = {
  async getPrimaryPlaylist(config = {}) {
    const response = await api.get('/playlists/primary', { ...config, skipAuth: true });
    return response.data;
  },

  async getPrimarySongs(params = {}, config = {}) {
    const response = await api.get('/playlists/primary/songs', { ...config, params, skipAuth: true });
    return response.data;
  },

  async getRandomPrimarySong(params = {}, config = {}) {
    const response = await api.get('/playlists/primary/songs/random', { ...config, params, skipAuth: true });
    return response.data.song;
  },

  async getAllPlaylists() {
    const response = await api.get('/playlists');
    return response.data;
  },

  async getPlaylistById(id) {
    const response = await api.get(`/playlists/${id}`);
    return response.data;
  },

  async createPlaylist(data) {
    const response = await api.post('/playlists', data);
    return response.data;
  },

  async addSong(playlistId, songData) {
    const response = await api.post(`/playlists/${playlistId}/songs`, songData);
    return response.data;
  },

  async batchAddSongs(playlistId, songs) {
    const response = await api.post(`/playlists/${playlistId}/songs/batch`, { songs });
    return response.data;
  },

  async updateSong(songId, songData) {
    const response = await api.put(`/playlists/songs/${songId}`, songData);
    return response.data;
  },

  async deleteSong(songId) {
    const response = await api.delete(`/playlists/songs/${songId}`);
    return response.data;
  },

  async getAllTags() {
    const response = await api.get('/playlists/tags');
    return response.data;
  },

  async createTag(tagData) {
    const response = await api.post('/playlists/tags', tagData);
    return response.data;
  },

  async updateTag(id, tagData) {
    const response = await api.put(`/playlists/tags/${id}`, tagData);
    return response.data;
  },

  async deleteTag(id) {
    const response = await api.delete(`/playlists/tags/${id}`);
    return response.data;
  }
};

export const prizeService = {
  async getAllPrizes() {
    const response = await api.get('/prizes');
    return response.data.data || response.data;
  },

  async getPrizeById(id) {
    const response = await api.get(`/prizes/${id}`);
    return response.data.data || response.data;
  },

  async redeemPrize(prizeId, options = 'points') {
    const payload = typeof options === 'string'
      ? { prizeId, currency_type: options }
      : {
          prizeId,
          currency_type: options.currencyType || options.currency_type || 'points',
          prize_option_id: options.prizeOptionId || options.prize_option_id || null,
          quantity: options.quantity || 1,
          address_id: options.addressId || options.address_id || null,
          remark: options.remark || ''
        };
    const response = await api.post('/prizes/redeem', payload);
    return response.data.data || response.data;
  },

  async getCart() {
    const response = await api.get('/prizes/cart');
    return response.data.data || response.data;
  },

  async addCartItem(data) {
    const response = await api.post('/prizes/cart/items', data);
    return response.data.data || response.data;
  },

  async updateCartItem(id, data) {
    const response = await api.put(`/prizes/cart/items/${id}`, data);
    return response.data.data || response.data;
  },

  async deleteCartItem(id) {
    const response = await api.delete(`/prizes/cart/items/${id}`);
    return response.data.data || response.data;
  },

  async clearCart() {
    const response = await api.delete('/prizes/cart');
    return response.data.data || response.data;
  },

  async checkoutCart(data = {}) {
    const response = await api.post('/prizes/cart/checkout', data);
    return response.data.data || response.data;
  },

  async getShippingAddresses() {
    const response = await api.get('/prizes/shipping-addresses');
    return response.data.data || response.data;
  },

  async createShippingAddress(data) {
    const response = await api.post('/prizes/shipping-addresses', data);
    return response.data.data || response.data;
  },

  async updateShippingAddress(id, data) {
    const response = await api.put(`/prizes/shipping-addresses/${id}`, data);
    return response.data.data || response.data;
  },

  async deleteShippingAddress(id) {
    const response = await api.delete(`/prizes/shipping-addresses/${id}`);
    return response.data;
  },

  async setDefaultShippingAddress(id) {
    const response = await api.put(`/prizes/shipping-addresses/${id}/default`);
    return response.data;
  },

  async getUserRedemptions() {
    const response = await api.get('/prizes/user/redemptions');
    return response.data.data || response.data;
  },

  async getUserOrders() {
    const response = await api.get('/prizes/user/orders');
    return response.data.data || response.data;
  },

  async getUserOrder(id) {
    const response = await api.get(`/prizes/user/orders/${id}`);
    return response.data.data || response.data;
  },

  async getAdminItems() {
    const response = await api.get('/prizes/admin/items');
    return response.data;
  },

  async createAdminItem(data) {
    const response = await api.post('/prizes/admin/items', data);
    return response.data;
  },

  async updateAdminItem(id, data) {
    const response = await api.put(`/prizes/admin/items/${id}`, data);
    return response.data;
  },

  async updateAdminItemOrder(itemIds) {
    const response = await api.put('/prizes/admin/items/order', { item_ids: itemIds });
    return response.data;
  },

  async deleteAdminItem(id) {
    const response = await api.delete(`/prizes/admin/items/${id}`);
    return response.data;
  },

  async uploadPrizeImage(id, file) {
    const formData = new FormData();
    formData.append('image', file, file.name);
    const response = await api.post(`/prizes/admin/items/${id}/images`, formData);
    return response.data;
  },

  async uploadPrizeOptionImage(id, optionId, file) {
    const formData = new FormData();
    formData.append('image', file, file.name);
    const response = await api.put(`/prizes/admin/items/${id}/options/${optionId}/image`, formData);
    return response.data;
  },

  async deletePrizeOptionImage(id, optionId) {
    const response = await api.delete(`/prizes/admin/items/${id}/options/${optionId}/image`);
    return response.data;
  },

  async updatePrizeImageOrder(id, imageIds) {
    const response = await api.put(`/prizes/admin/items/${id}/images/order`, { image_ids: imageIds });
    return response.data;
  },

  async deletePrizeImage(id, imageId) {
    const response = await api.delete(`/prizes/admin/items/${id}/images/${imageId}`);
    return response.data;
  },

  async getAdminRedemptions(params = {}) {
    const response = await api.get('/prizes/admin/redemptions', { params });
    return response.data;
  },

  async updateRedemptionStatus(id, status, reason = '') {
    const response = await api.put(`/prizes/admin/redemptions/${id}/status`, { status, reason });
    return response.data;
  },

  async getAdminOrders(params = {}) {
    const response = await api.get('/prizes/admin/orders', { params });
    return response.data;
  },

  async getAdminOrder(id) {
    const response = await api.get(`/prizes/admin/orders/${id}`);
    return response.data;
  },

  async updateOrderStatus(id, status, reason = '') {
    const response = await api.put(`/prizes/admin/orders/${id}/status`, { status, reason });
    return response.data;
  }
};

export const marshmallowService = {
  async createMarshmallow(data) {
    const response = await api.post('/marshmallows', data);
    return response.data;
  },

  async getMyMarshmallows() {
    const response = await api.get('/marshmallows/my');
    return response.data;
  },

  async bindMarshmallow(marshmallowId) {
    const response = await api.post('/marshmallows/bind', { marshmallowId });
    return response.data;
  },

  async getAllMarshmallows() {
    const response = await api.get('/marshmallows/admin');
    return response.data;
  },

  async replyMarshmallow(id, replyContent) {
    const response = await api.put(`/marshmallows/${id}/reply`, { reply_content: replyContent });
    return response.data;
  },

  async markAsRead(id) {
    const response = await api.post(`/marshmallows/${id}/read`);
    return response.data;
  },

  async deleteMarshmallows(ids) {
    const response = await api.post('/marshmallows/delete', { ids });
    return response.data;
  }
};

export const bilibiliService = {
  async getInfo() {
    // 直接从后端获取，后端已实现缓存
    const response = await api.get('/bilibili/info');
    return response.data;
  }
};

export const bilibiliBindingService = {
  /**
   * 检查用户是否有权限使用需要绑定的功能（截图、盲盒统计、礼物展示）
   * @returns {Promise<{hasAccess: boolean, reason?: string, code?: string, isAdmin?: boolean}>}
   */
  async checkFeatureAccess() {
    const response = await api.get('/bilibili-binding/check-feature-access');
    return response.data;
  },

  /**
   * 获取绑定状态
   */
  async getBindingStatus() {
    const response = await api.get('/bilibili-binding/status');
    return response.data;
  }
};

export { default as botService } from './botService';
export { default as permissionService } from './permissionService';
export { default as blindboxService } from './blindboxService';
export { default as websocketService } from './websocketService';
export { default as giftScreenshotService } from './giftScreenshotService';
export { default as pointsService } from './pointsService';
export { default as movieTicketsService } from './movieTicketsService';
